import { InjectionToken } from '@angular/core';
import {
  AUTH_TAB_SYNC_CHANNEL_NAME,
  AUTH_TAB_SYNC_FALLBACK_STORAGE_KEY,
  AUTH_TAB_SYNC_REPLY_TIMEOUT_MS,
} from './auth-tab-sync.constants';
import {
  AuthTabSyncMessage,
  isAuthTabSyncMessage,
} from './auth-tab-sync.messages';

export type AuthTabSyncListener = (message: AuthTabSyncMessage) => void;

/**
 * Canal entre les onglets de l'application. Un onglet ne reçoit jamais ses
 * propres messages (propriété commune à BroadcastChannel et à l'événement
 * `storage`).
 */
export interface AuthTabSyncTransport {
  post(message: AuthTabSyncMessage): void;
  listen(listener: AuthTabSyncListener): void;
  close(): void;
}

/** Canal nominal : `BroadcastChannel`, en mémoire, rien n'est écrit sur disque. */
export class BroadcastChannelTransport implements AuthTabSyncTransport {
  private readonly listeners: ((event: MessageEvent<unknown>) => void)[] = [];

  constructor(private readonly channel: BroadcastChannel) {}

  post(message: AuthTabSyncMessage): void {
    this.channel.postMessage(message);
  }

  listen(listener: AuthTabSyncListener): void {
    const handler = (event: MessageEvent<unknown>) => {
      if (isAuthTabSyncMessage(event.data)) {
        listener(event.data);
      }
    };
    this.listeners.push(handler);
    this.channel.addEventListener('message', handler);
  }

  close(): void {
    this.listeners.forEach((handler) =>
      this.channel.removeEventListener('message', handler)
    );
    this.listeners.length = 0;
    this.channel.close();
  }
}

/**
 * Repli pour les navigateurs sans BroadcastChannel : le message transite par
 * l'événement `storage`, émis dans les AUTRES onglets quand une clé du
 * localStorage change. Il n'y séjourne que le temps de l'écriture.
 */
export class StorageEventTransport implements AuthTabSyncTransport {
  private readonly listeners: ((event: StorageEvent) => void)[] = [];

  constructor(
    private readonly storage: Storage,
    private readonly eventTarget: Window
  ) {}

  post(message: AuthTabSyncMessage): void {
    this.storage.setItem(
      AUTH_TAB_SYNC_FALLBACK_STORAGE_KEY,
      JSON.stringify(message)
    );
    this.storage.removeItem(AUTH_TAB_SYNC_FALLBACK_STORAGE_KEY);
  }

  listen(listener: AuthTabSyncListener): void {
    const handler = (event: StorageEvent) => {
      if (event.key !== AUTH_TAB_SYNC_FALLBACK_STORAGE_KEY) {
        return;
      }
      const message = parseMessage(event.newValue);
      if (message) {
        listener(message);
      }
    };
    this.listeners.push(handler);
    this.eventTarget.addEventListener('storage', handler);
  }

  close(): void {
    this.listeners.forEach((handler) =>
      this.eventTarget.removeEventListener('storage', handler)
    );
    this.listeners.length = 0;
  }
}

/**
 * Lecture d'un message du repli : l'effacement qui suit chaque écriture émet
 * une valeur nulle, et une valeur illisible n'est pas un message de
 * l'application. Les deux sont ignorés.
 */
function parseMessage(rawValue: string | null): AuthTabSyncMessage | null {
  if (!rawValue) {
    return null;
  }
  try {
    const value: unknown = JSON.parse(rawValue);
    return isAuthTabSyncMessage(value) ? value : null;
  } catch {
    return null;
  }
}

export function createAuthTabSyncTransport(): AuthTabSyncTransport {
  return typeof BroadcastChannel === 'function'
    ? new BroadcastChannelTransport(
        new BroadcastChannel(AUTH_TAB_SYNC_CHANNEL_NAME)
      )
    : new StorageEventTransport(localStorage, window);
}

/**
 * Fabrique du canal, appelée au premier besoin (pas à l'injection) : un
 * service qui ne synchronise rien n'ouvre aucun canal.
 */
export const AUTH_TAB_SYNC_TRANSPORT_FACTORY = new InjectionToken<
  () => AuthTabSyncTransport
>('AUTH_TAB_SYNC_TRANSPORT_FACTORY', {
  providedIn: 'root',
  factory: () => createAuthTabSyncTransport,
});

/** Délai d'attente de la réponse d'un autre onglet (surchargeable en test). */
export const AUTH_TAB_SYNC_REPLY_TIMEOUT = new InjectionToken<number>(
  'AUTH_TAB_SYNC_REPLY_TIMEOUT',
  { providedIn: 'root', factory: () => AUTH_TAB_SYNC_REPLY_TIMEOUT_MS }
);
